import { useLingui } from '@lingui/react/macro';
import { cn } from '@worldcoo/ui/lib/utils';
import { useNgoAreaUrl } from '@/lib/ngo-area';
import { useScrollStep } from '../use-scroll-step';
import { PillLink } from '../primitives';
import { NGO_APPLICATION_FORM_URL } from '../external-links';

type Step = {
	/** The big ordinal the card prints in its top-right corner. */
	number: string;
	title: string;
	body: string;
	illustration: string;
	/** Surface + text colours; each step is a different card in the hand-off. */
	surface: string;
	tone: string;
	/** The last card carries a CTA instead of being a step in the process. */
	cta?: string;
	/** Where that CTA goes. Absent on every other card, which has no CTA to send anywhere. */
	ctaHref?: string;
};

/**
 * One card in the deck. The hand-off draws the mobile card at 275x330 and the desktop one at 354x424 —
 * the same card at two scales, which is why every inner size is a breakpoint pair rather than one
 * value in a scaled wrapper (a CSS `scale` would blur the type).
 */
function StepCard({
	step,
	className,
	style,
	onAdvance,
}: {
	step: Step;
	className?: string;
	style?: React.CSSProperties;
	/** Absent on the last card, which is the end of the path and has nowhere to advance to. */
	onAdvance?: () => void;
}) {
	return (
		<article
			onClick={onAdvance}
			className={cn(
				'flex h-[330px] w-[275px] flex-col justify-end overflow-hidden rounded-[19px] p-8',
				'lg:h-[424px] lg:w-[354px] lg:rounded-3xl lg:p-10',
				onAdvance && 'cursor-pointer',
				step.surface,
				step.tone,
				className,
			)}
			style={style}
		>
			<img
				src={step.illustration}
				alt=""
				aria-hidden
				className="absolute top-6 left-8 h-auto w-[150px] lg:top-8 lg:left-10 lg:w-[197px]"
			/>
			<span className="absolute top-6 right-6 text-xl leading-[1.5] tracking-[-0.02em] opacity-90 lg:top-8 lg:right-8 lg:text-2xl">
				{step.number}
			</span>
			<div className="relative flex flex-col gap-1">
				<h3 className="text-xl leading-[1.2] font-medium tracking-[-0.02em] lg:text-2xl">{step.title}</h3>
				<p className="text-sm leading-[1.5] lg:text-base">{step.body}</p>
				{step.cta &&
					step.ctaHref && (
						// A real anchor, not the span it used to be: this is the section's exit into the NGO area,
						// and text that only LOOKED like a link went nowhere for everyone and did not exist at all
						// for the keyboard. `stopPropagation` guards the case the ticket warns about — today the
						// last card takes no `onAdvance`, so nothing would swallow the click, but a card handler
						// added later would, and the failure would be silent.
						<a
							href={step.ctaHref}
							onClick={(event) => event.stopPropagation()}
							className="mt-3 w-fit rounded-sm text-[15px] font-medium tracking-[-0.02em] underline focus-visible:ring-2 focus-visible:ring-current focus-visible:outline-none"
						>
							{step.cta}
						</a>
					)}
			</div>
		</article>
	);
}

/**
 * The 01–04 progress rail: a hairline track with the active step's segment in brand orange.
 *
 * IT TURNS, and that is not decoration. Beside the deck it is a 122px COLUMN; stacked under the deck on
 * mobile it is a ROW, because a column there made the pinned content 132px taller than it had room for
 * and `overflow-hidden` cropped the bottom of the rail — the reader saw 01 and 02 as ghosts and never
 * the active ordinal at all (the reported bug). A row costs ~24px instead of 132 and reads as a
 * progress bar under the card, the same idiom the About timeline's year rail uses at that width.
 *
 * The segment's size and offset are ONE pair of numbers in custom properties, spent on `width`/`left`
 * in the row and on `height`/`top` in the column. Written twice as inline styles they would have been
 * two sources of truth for the same fraction, and a percentage resolves against the axis it is used
 * on, so the pair needs no conversion.
 *
 * The ordinals are the section's PRIMARY control, and the only one the keyboard has: the cards
 * themselves stay unfocusable so the four steps are not each reachable twice in the tab order, the
 * same trade the About timeline's rail documents.
 */
function StepRail({ steps, active, onSelect }: { steps: Step[]; active: number; onSelect: (index: number) => void }) {
	return (
		<div className="flex shrink-0 flex-col-reverse items-center gap-2 lg:flex-row lg:items-center lg:gap-1.5">
			{/* `self-stretch` in the stacked case, so the track spans the ordinals' own width and each
			    segment lands under its number without either being measured. */}
			<div className="bg-ink/10 relative h-0.5 self-stretch rounded-full lg:h-[122px] lg:w-0.5 lg:self-auto">
				<div
					className="bg-brand absolute top-0 left-[var(--rail-offset)] h-full w-[var(--rail-segment)] rounded-full transition-all duration-500 lg:top-[var(--rail-offset)] lg:left-0 lg:h-[var(--rail-segment)] lg:w-full"
					style={
						{
							'--rail-segment': `${100 / steps.length}%`,
							'--rail-offset': `${(active * 100) / steps.length}%`,
						} as React.CSSProperties
					}
				/>
			</div>
			<ol className="flex gap-3 lg:flex-col lg:gap-2">
				{steps.map((step, index) => (
					<li key={step.number}>
						<button
							type="button"
							onClick={() => onSelect(index)}
							aria-current={index === active ? 'step' : undefined}
							className={cn(
								'focus-visible:ring-ink cursor-pointer rounded-sm text-base leading-[1.5] font-medium tracking-[-0.02em] transition-opacity focus-visible:ring-2 focus-visible:outline-none',
								index === active ? 'opacity-100' : 'opacity-20 hover:opacity-60',
							)}
						>
							{step.number}
						</button>
					</li>
				))}
			</ol>
		</div>
	);
}

/**
 * "¿Cómo empezar a colaborar?" — the four-step onboarding path.
 *
 * Scroll-driven at BOTH breakpoints, which the mobile frame confirms: copy, deck and rail are each
 * their own sticky column there too, simply stacked instead of set side by side. So this is one
 * layout that goes from a row to a column, not two different sections.
 */
export function HowToCollaborate() {
	const { t } = useLingui();
	const ngoArea = useNgoAreaUrl();

	const steps: Step[] = [
		{
			number: '01',
			title: t`Send your application`,
			body: t`Fill in the form with your organisation's basic information.`,
			illustration: '/redesign/partner-ngos/illo-send.svg',
			surface: 'bg-apricot',
			tone: 'text-black',
		},
		{
			number: '02',
			title: t`Assessment`,
			body: t`Our team will check that you meet the minimum transparency criteria. If something is missing, don't worry — we'll tell you what to correct so you can apply again.`,
			illustration: '/redesign/partner-ngos/illo-review.svg',
			surface: 'bg-ink',
			tone: 'text-white',
		},
		{
			number: '03',
			title: t`Agreement and activation`,
			body: t`We sign the collaboration agreement and you're all set!`,
			illustration: '/redesign/partner-ngos/illo-agreement.svg',
			surface: 'bg-brand',
			tone: 'text-black',
		},
		{
			number: '04',
			title: t`Still have questions before you start?`,
			body: t`Join the next live information session.`,
			illustration: '/redesign/partner-ngos/illo-folder.svg',
			surface: 'bg-olive',
			tone: 'text-black',
			cta: t`Register`,
			ctaHref: ngoArea('/onboarding'),
		},
	];

	const { ref, active, enabled, goTo } = useScrollStep(steps.length);
	const last = steps.length - 1;

	return (
		/* One viewport to pin in, plus 70vh of travel per card after the first. A full viewport per card
		   (the obvious `steps * 100vh`) makes the section four screens tall and the page reads as stuck. */
		<section
			ref={ref}
			className="relative"
			// No extra travel when nothing is reading it (reduced motion): the section would otherwise be
			// four screens of scrolling that never change the card.
			style={enabled ? { height: `calc(100vh + ${(steps.length - 1) * 70}vh)` } : undefined}
		>
			{/* `min-h-screen`, not `h-screen`: the pinned column is what `useScrollStep` measures its travel
			    against, and a fixed viewport height made a column that outgrew the viewport get CROPPED by the
			    clip below rather than counted. The clip is for the deck's rotated cards, which reach past the
			    box that lays them out — it was never meant to hide a control. */}
			<div className="sticky top-0 flex min-h-screen items-center overflow-hidden">
				{/* `gap-12` stacked, not the desktop `gap-10`: the deck's cards are rotated, so they reach past
					    the box that lays them out and a 24px gap left them almost touching the step rail below. */}
				<div className="mx-auto flex w-full max-w-[1440px] flex-col items-center gap-12 px-4 lg:flex-row lg:items-center lg:justify-between lg:gap-10 lg:px-16">
					<div className="flex max-w-[646px] flex-col items-start gap-4">
						<h2 className="text-3xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
							{t`How do you start collaborating?`}
						</h2>
						<p className="text-base leading-[1.5]">
							{t`If you're looking for funding for your social projects, the Solidarity Round-Up and our digital tools connect you with the customers and employees of large companies. Your projects join our database so partner companies can select them according to their values.`}
						</p>
						<PillLink variant="ink" href={NGO_APPLICATION_FORM_URL}>{t`Register your organisation`}</PillLink>
					</div>

					<div className="relative h-[330px] w-[275px] shrink-0 lg:h-[424px] lg:w-[354px]">
						{steps.map((step, index) => {
							const offset = index - active;
							const isPast = offset < 0;
							return (
								<StepCard
									key={step.number}
									step={step}
									// Only the card on top is clickable — `pointerEvents` below already sees to that — and
									// only while there is a step after it to reach.
									onAdvance={offset === 0 && index < last ? () => goTo(index + 1) : undefined}
									className="absolute inset-0 transition-all duration-500 ease-out"
									style={{
										// Cards ahead of the active one fan out behind it; cards already dealt slide up
										// and fade, so the deck reads as a stack being worked through.
										transform: isPast
											? 'translateY(-32px) scale(0.94) rotate(-6deg)'
											: `translateY(${offset * 12}px) rotate(${offset * 4}deg) scale(${1 - offset * 0.03})`,
										opacity: isPast ? 0 : 1,
										zIndex: steps.length - Math.abs(offset),
										pointerEvents: offset === 0 ? 'auto' : 'none',
									}}
								/>
							);
						})}
					</div>

					<StepRail steps={steps} active={active} onSelect={goTo} />
				</div>
			</div>
		</section>
	);
}
